export type DeploymentEnvironment = "local" | "staging" | "production";

export interface RuntimeEnvironment {
  environment: DeploymentEnvironment;
  gitSha: string;
  releaseId: string;
  schemaVersion: string;
  fixtureVersion: string;
  apiUrl: string;
  supabaseUrl: string;
  assetBaseUrl: string;
  publicAppUrl: string;
}

type EnvironmentSource = Record<string, string | undefined>;

const LNP_PRODUCTION_PROJECT_REF = "xqcbhhpgqiioggywqiet";
const WORKBENCH_PRODUCTION_PROJECT_REF = "leqccckforxrqcjconhd";
const LIVE_PROJECT_REFS = new Set([
  LNP_PRODUCTION_PROJECT_REF,
  WORKBENCH_PRODUCTION_PROJECT_REF,
]);
const PRODUCTION_API_HOST = "api.locatenowatl.com";
const PRODUCTION_APP_HOST = "app.locatenowatl.com";
const PRODUCTION_STORAGE_HOST =
  "locatenow-apartment-images.s3.us-east-2.amazonaws.com";
const FULL_SHA_RE = /^[0-9a-f]{40}$/;
const PROJECT_REF_RE = /^[a-z0-9]{20}$/;

function required(source: EnvironmentSource, name: string): string {
  const value = (source[name] || "").trim();
  if (!value) throw new Error(`${name} is required`);
  return value;
}

function parseUrl(value: string, name: string): URL {
  try {
    return new URL(value);
  } catch {
    throw new Error(`${name} must be an absolute URL`);
  }
}

function supabaseProjectRef(url: URL): string | null {
  const suffix = ".supabase.co";
  if (!url.hostname.endsWith(suffix)) return null;
  const projectRef = url.hostname.slice(0, -suffix.length);
  return PROJECT_REF_RE.test(projectRef) ? projectRef : null;
}

export function validateRuntimeEnvironment(
  source: EnvironmentSource
): RuntimeEnvironment {
  const environment = required(source, "REACT_APP_ENVIRONMENT");
  if (!(["local", "staging", "production"] as string[]).includes(environment)) {
    throw new Error(
      "REACT_APP_ENVIRONMENT must be exactly local, staging, or production"
    );
  }

  // Vercel's own VERCEL_ENV is provider metadata (preview/production), not
  // product environment identity and is intentionally ignored.
  const legacy = ["REACT_APP_DEPLOY_ENV"]
    .map((name) => (source[name] || "").trim())
    .filter(Boolean);
  if (legacy.some((value) => value !== environment)) {
    throw new Error("frontend deployment environment identity is contradictory");
  }

  const gitSha = required(source, "REACT_APP_GIT_SHA").toLowerCase();
  if (!FULL_SHA_RE.test(gitSha)) {
    throw new Error("REACT_APP_GIT_SHA must be a full 40-character commit SHA");
  }

  const apiUrl = parseUrl(required(source, "REACT_APP_API_URL"), "REACT_APP_API_URL");
  const supabaseUrl = parseUrl(
    required(source, "REACT_APP_SUPABASE_URL"),
    "REACT_APP_SUPABASE_URL"
  );
  const assetBaseUrl = parseUrl(
    required(source, "REACT_APP_PUBLIC_ASSET_BASE_URL"),
    "REACT_APP_PUBLIC_ASSET_BASE_URL"
  );
  const publicAppUrl = parseUrl(
    required(source, "REACT_APP_PUBLIC_APP_URL"),
    "REACT_APP_PUBLIC_APP_URL"
  );
  const projectRef = supabaseProjectRef(supabaseUrl);

  if (environment === "production") {
    if (projectRef !== LNP_PRODUCTION_PROJECT_REF) {
      throw new Error("production frontend requires the LocateNowPro production project");
    }
    if (apiUrl.hostname !== PRODUCTION_API_HOST) {
      throw new Error("production frontend requires the production API host");
    }
    if (assetBaseUrl.hostname !== PRODUCTION_STORAGE_HOST) {
      throw new Error("production frontend requires the production storage host");
    }
    if (publicAppUrl.hostname !== PRODUCTION_APP_HOST) {
      throw new Error("production frontend requires the production app host");
    }
  } else {
    if (projectRef && LIVE_PROJECT_REFS.has(projectRef)) {
      throw new Error("known live Supabase project is forbidden outside production");
    }
    if (apiUrl.hostname === PRODUCTION_API_HOST) {
      throw new Error("production API host is forbidden outside production");
    }
    if (assetBaseUrl.hostname === PRODUCTION_STORAGE_HOST) {
      throw new Error("production storage host is forbidden outside production");
    }
    if (publicAppUrl.hostname === PRODUCTION_APP_HOST) {
      throw new Error("production app host is forbidden outside production");
    }
    if (environment === "staging") {
      const stagingRef = required(
        source,
        "REACT_APP_STAGING_SUPABASE_PROJECT_REF"
      ).toLowerCase();
      if (!PROJECT_REF_RE.test(stagingRef) || LIVE_PROJECT_REFS.has(stagingRef)) {
        throw new Error("staging project ref must be a valid non-live project");
      }
      if (projectRef !== stagingRef) {
        throw new Error("staging Supabase URL must match the explicit staging ref");
      }
    }
  }

  return {
    environment: environment as DeploymentEnvironment,
    gitSha,
    releaseId: required(source, "REACT_APP_RELEASE_ID"),
    schemaVersion: required(source, "REACT_APP_SCHEMA_VERSION"),
    fixtureVersion: required(source, "REACT_APP_FIXTURE_VERSION"),
    apiUrl: apiUrl.toString().replace(/\/$/, ""),
    supabaseUrl: supabaseUrl.toString().replace(/\/$/, ""),
    assetBaseUrl: assetBaseUrl.toString().replace(/\/$/, ""),
    publicAppUrl: publicAppUrl.toString().replace(/\/$/, ""),
  };
}

export const runtimeEnvironment = validateRuntimeEnvironment(process.env);
